// components/LocationsPage.tsx
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Button,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Rating,
} from "@mui/material";
import NearMeIcon from "@mui/icons-material/NearMe";
import { GeoPoint } from "firebase/firestore";

import type { Offer } from "../../lib/types/detailsTypes";
import { fetchLocationInfo, fetchOffers } from "../offers/offersFirestore";
import OfferCard from "../offers/offerCard";

interface StoreHours {
  [day: string]: {
    open: string;
    close: string;
  };
}

interface LocationInfo {
  name: string;
  distance: number;
  storeHours: StoreHours;
  offers: Offer[];
  googleMapsLink?: string;
  googleMapsId?: string;
  rating?: number;
}

const LocationPage: React.FC = () => {
  const { locationId } = useParams<{ locationId: string }>();
  const [locationInfo, setLocationInfo] = useState<LocationInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [offers, setOffers] = useState<Offer[]>([]);

  // Get today's day name (e.g., "Monday")
  const today = new Date().toLocaleString("en-US", { weekday: "long" });

  // Helper to check if the store is open now
  const isStoreOpen = (openTime: string, closeTime: string): boolean => {
    const now = new Date();
    const [openHour, openMinute] = openTime.split(":").map(Number);
    const [closeHour, closeMinute] = closeTime.split(":").map(Number);
    const openDate = new Date(now);
    openDate.setHours(openHour, openMinute, 0, 0);
    const closeDate = new Date(now);
    closeDate.setHours(closeHour, closeMinute, 0, 0);
    return now >= openDate && now <= closeDate;
  };

  useEffect(() => {
    if (locationId) {
      (async () => {
        try {
          // Use a temporary center; later replace with user's actual location if needed.
          const center = new GeoPoint(49.1526159, -122.806208);
          const data = await fetchLocationInfo(locationId, center);
          setLocationInfo(data);
          setLoading(false);

          const fetchedOffers = await fetchOffers({ locationId: locationId });
          setOffers(fetchedOffers);
        } catch (error) {
          console.error("Error fetching location info:", error);
          setLoading(false);
        }
      })();
    }
  }, [locationId]);

  if (loading) {
    return (
      <Container sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!locationInfo) {
    return (
      <Container sx={{ py: 4 }}>
        <Typography variant="h6" color="error">
          Unable to load location details.
        </Typography>
      </Container>
    );
  }

  const { name, distance, storeHours, googleMapsLink, googleMapsId, rating } =
    locationInfo;

  // Get today's hours from storeHours
  const todayHours = storeHours[today];
  // Check if hours should be displayed; if both open and close are "00:00" or "0000", consider it invalid.
  const validHours =
    todayHours &&
    !(
      (todayHours.open === "00:00" || todayHours.open === "0000") &&
      (todayHours.close === "00:00" || todayHours.close === "0000")
    );

  // Compute open status based on valid hours; default to "Closed" if hours are not valid.
  const openStatus =
    validHours && isStoreOpen(todayHours.open, todayHours.close)
      ? "Open"
      : "Closed";

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header Card with location details */}
      <Card
        sx={{
          mb: 3,
          backgroundColor: "grey.900",
          color: "common.white",
          p: 2,
        }}
      >
        <CardContent sx={{ p: 1 }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 500 }}>
            {name}
          </Typography>

          {/* Clickable distance indicator */}
          {googleMapsLink ? (
            <Button
              variant="contained"
              color="primary"
              component="a"
              href={googleMapsLink}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                textTransform: "none",
                display: "flex",
                alignItems: "center",
                gap: 1,
                mt: 1,
                px: 2,
                py: 0.5,
              }}
            >
              <NearMeIcon />
              <Typography variant="body1">
                {distance < 10 ? distance.toFixed(2) : distance.toFixed(1)} km
                away
              </Typography>
            </Button>
          ) : (
            <Box display="flex" alignItems="center" mt={1}>
              <NearMeIcon sx={{ mr: 0.5 }} />
              <Typography variant="body1">
                {distance < 10 ? distance.toFixed(2) : distance.toFixed(1)} km
                away
              </Typography>
            </Box>
          )}

          {/* Store Hours with status chip in one line */}
          <Typography
            variant="body1"
            component="div" // Changed to "div" to avoid nesting issues
            sx={{ mt: 2, display: "flex", alignItems: "center" }}
          >
            Store Hours:{" "}
            {validHours ? (
              <>
                {todayHours.open} – {todayHours.close}
                <Chip
                  label={openStatus}
                  color={openStatus === "Open" ? "success" : "error"}
                  size="small"
                  sx={{ ml: 1 }}
                />
              </>
            ) : (
              <Chip label="Closed" color="error" size="small" sx={{ ml: 1 }} />
            )}
          </Typography>

          {/* Rating if available */}
          {googleMapsId && rating !== undefined && (
            <Box display="flex" alignItems="center" sx={{ mt: 1 }}>
              <Rating value={rating} precision={0.1} readOnly size="small" />
              <Typography variant="body2" sx={{ ml: 0.5 }}>
                {rating.toFixed(1)} / 5
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Offers Card */}
      <Card sx={{ backgroundColor: "grey.900", color: "common.white" }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Active Promotions
          </Typography>
          {offers.length === 0 ? (
            <Typography variant="body2" color="grey.400">
              No active promotions for this location.
            </Typography>
          ) : (
            offers.map((offer) => (
              <Box key={offer.id} sx={{ cursor: "pointer" }}>
                <OfferCard offer={offer} />
              </Box>
            ))
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default LocationPage;
